import React, { useState } from 'react';
import { Paper, Tabs, Tab, TabPanel } from '@kambi/spark';

import classes from '../../../App.module.css';
import CodeBlock from 'components/layout/code-block/CodeBlock';
import {
    OutcomeLabelMatch,
    OutcomeLabelOutright,
    OutcomeLabelOverUnder,
    OutcomeLabelHandicap,
    OutcomeLabelHead,
    OutcomeLabelMultiPosition,
    OutcomeLabelPosition,
    OutcomeLabelHalfTimeFullTime,
    OutcomeLabelPlayerOccurrenceLine,
    OutcomeLabelDefault,
} from './jsonExamples';

const OutcomeLabelTabs = () => {
    const [value, setValue] = useState(0);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    return (
        <div className={classes.Tabs}>
            <Paper>
                <Tabs
                    kind="inverted"
                    value={value}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label="Match" />
                    <Tab label="Outright" />
                    <Tab label="Over/Under" />
                    <Tab label="Handicap" />
                    <Tab label="Head" />
                    <Tab label="Multi Position" />
                    <Tab label="Position" />
                    <Tab label="Half Time/Full Time" />
                    <Tab label="Player Occurrence Line" />
                    <Tab label="Default" />
                </Tabs>
            </Paper>
            <TabPanel index={0} value={value} className={classes.TabPanel}>
                <p>
                    This label will be returned for the following betOffers:
                    <b> One Cross Two - id:2</b>, <b>Side Bets - id:9</b>,
                    <b> Three Way Handicap - id: 11</b>,
                    <b> Double Chance - id:12</b>.
                </p>
                <p style={{ marginBottom: '0' }}>
                    <i>
                        The final outcome label is based on the outcome type,
                        event homeName and event awayName. It accepts a callback
                        for translations, used to translate some static text and
                        the following <b>keys needs to be added</b> to your own
                        translations:
                    </i>
                </p>
                <ul>
                    <li>
                        <i>"outcomes.draw" : "Draw"</i>
                    </li>
                    <li>
                        <i>"outcomes.orDraw": "or Draw"</i>
                    </li>
                    <li>
                        <i>"outcomes.homeOrDraw": "Home or Draw"</i>
                    </li>
                    <li>
                        <i>"outcomes.or": "or"</i>
                    </li>
                    <li>
                        <i>"outcomes.homeOrAway": "Home or Away" </i>
                    </li>
                    <li>
                        <i>"outcomes.awayOrDraw": "Draw or Away"</i>
                    </li>
                </ul>
                <p>
                    <i>
                        If the callback isn't provided it will fallback to the
                        english text.
                    </i>
                </p>
                <CodeBlock
                    title={OutcomeLabelMatch.title}
                    expectedOutput={OutcomeLabelMatch.expectedOutput}
                    initialDisplayedCode={
                        OutcomeLabelMatch.initialDisplayedCode
                    }
                    moreCode={OutcomeLabelMatch.moreCode}
                />
            </TabPanel>
            <TabPanel index={1} value={value} className={classes.TabPanel}>
                <p>
                    This label will be returned for the following betOffer:
                    <b> Outright - id:4. </b>
                </p>
                <p>
                    <i>
                        If the betOffer has place: true, it will append the
                        betOffer type name after the outcome label.
                    </i>
                </p>
                <CodeBlock
                    title={OutcomeLabelOutright.title}
                    expectedOutput={OutcomeLabelOutright.expectedOutput}
                    initialDisplayedCode={
                        OutcomeLabelOutright.initialDisplayedCode
                    }
                    moreCode={OutcomeLabelOutright.moreCode}
                />
            </TabPanel>
            <TabPanel index={2} value={value} className={classes.TabPanel}>
                <p>
                    This label will be returned for the following betOffer:
                    <b> Over/Under - id:6</b> and{' '}
                    <b>Asian Over/Under - id:21</b>.
                </p>
                <p>
                    <i>
                        If the outcome has line, it will append the formatted
                        line after the outcome label.
                    </i>
                </p>
                <CodeBlock
                    title={OutcomeLabelOverUnder.title}
                    expectedOutput={OutcomeLabelOverUnder.expectedOutput}
                    initialDisplayedCode={
                        OutcomeLabelOverUnder.initialDisplayedCode
                    }
                    moreCode={OutcomeLabelOverUnder.moreCode}
                />
            </TabPanel>
            <TabPanel index={3} value={value} className={classes.TabPanel}>
                <p>
                    This label will be returned for the following betOffer:
                    <b> Handicap - id:1</b> and <b>Asian - id:7</b>.
                </p>
                <p>
                    <i>
                        It accepts a callback for translations, and a
                        enableHandicapPickem boolean. If the
                        enableHandicapPickem is true and outcome has line=0, it
                        will append after the outcome label the translation for
                        the "outcomes.pickemShort" key or "pk". And if the line
                        is gratter than 0, it will append the formatted line.
                    </i>
                </p>
                <p style={{ marginBottom: '0' }}>
                    <i>
                        The following <b>keys needs to be added</b> to your own
                        translations:
                    </i>
                </p>
                <ul>
                    <li>
                        <i>"outcomes.pickemShort" : "pk"</i>
                    </li>
                </ul>
                <p>
                    <i>
                        If the callback isn't provided it will fallback to the
                        english text.
                    </i>
                </p>
                <CodeBlock
                    title={OutcomeLabelHandicap.title}
                    expectedOutput={OutcomeLabelHandicap.expectedOutput}
                    initialDisplayedCode={
                        OutcomeLabelHandicap.initialDisplayedCode
                    }
                    moreCode={OutcomeLabelHandicap.moreCode}
                />
            </TabPanel>
            <TabPanel index={4} value={value} className={classes.TabPanel}>
                <p>
                    This label will be returned for the following betOffer:
                    <b> Head - id:13</b>.
                </p>
                <p>
                    <i>
                        Based on how many outcomes and participants are in the
                        betOffer, it will append after the outcome label either
                        the first participant, or the first two participants or
                        all the participants.
                    </i>
                </p>
                <CodeBlock
                    title={OutcomeLabelHead.title}
                    expectedOutput={OutcomeLabelHead.expectedOutput}
                    initialDisplayedCode={OutcomeLabelHead.initialDisplayedCode}
                    moreCode={OutcomeLabelHead.moreCode}
                />
            </TabPanel>
            <TabPanel index={5} value={value} className={classes.TabPanel}>
                <p>
                    This label will be returned for the following betOffer:
                    <b> Multiposition - id:19</b> - is specific only to horse
                    racing and greyhounds betOffer, for the Forecast/Tricast
                    outcomes.
                </p>
                <p>
                    <i>
                        In order to determine the correct label an object
                        "selections" is <b>required</b>, in the the following
                        format: {`{eventId}_{participantId}: {position}`} -
                        where position can be: "FIRST", "SECOND", "THIRD",
                        "ANY".
                    </i>
                </p>
                <p>
                    <i>
                        The offering API usually provides two types of betoffers
                        for multiposiion. One with the property placeLimit = 2
                        and one with placeLimit = 3. For a selections object
                        with 2 non-"ANY" (either "FIRST", "SECOND", "THIRD")
                        positions we should send to the outcomeLabel function
                        the betoffer with placeLimit 2 and for 3 non-"ANY"
                        selections we should send the betoffer with placeLimit 3
                    </i>
                </p>
                <p style={{ marginBottom: '0' }}>
                    <i>
                        It accepts a callback for translations, used to
                        translate some static text and the following
                        <b>keys needs to be added</b> to your own translations:
                    </i>
                </p>
                <ul>
                    <li>
                        <i>
                            "betoffer.multiPosition.type.combinationForecast":
                            "Combination Forecast"
                        </i>
                    </li>
                    <li>
                        <i>
                            "betoffer.multiPosition.type.combinationTricast":
                            "Combination Tricast"
                        </i>
                    </li>
                    <li>
                        <i>
                            "betoffer.multiPosition.type.forecast": "Forecast"
                        </i>
                    </li>
                    <li>
                        <i>
                            "betoffer.multiPosition.type.reverseForecast":
                            "Reverse Forecast"
                        </i>
                    </li>
                    <li>
                        <i>"betoffer.multiPosition.type.tricast": "Tricast"</i>
                    </li>
                    <li>
                        <i>"bethistory.bets": "bets"</i>
                    </li>
                </ul>
                <p>
                    <i>
                        If the callback isn't provided it will fallback to the
                        english text.
                    </i>
                </p>
                <CodeBlock
                    title={OutcomeLabelMultiPosition.title}
                    expectedOutput={OutcomeLabelMultiPosition.expectedOutput}
                    initialDisplayedCode={
                        OutcomeLabelMultiPosition.initialDisplayedCode
                    }
                    moreCode={OutcomeLabelMultiPosition.moreCode}
                />
            </TabPanel>
            <TabPanel index={6} value={value} className={classes.TabPanel}>
                <p>
                    This label will be returned for the following betOffer:
                    <b> Position - id:117</b>.
                </p>
                <p>
                    <i>
                        If the betOffer has description it will append it after
                        the outcome label.
                    </i>
                </p>
                <CodeBlock
                    title={OutcomeLabelPosition.title}
                    expectedOutput={OutcomeLabelPosition.expectedOutput}
                    initialDisplayedCode={
                        OutcomeLabelPosition.initialDisplayedCode
                    }
                    moreCode={OutcomeLabelPosition.moreCode}
                />
            </TabPanel>
            <TabPanel index={7} value={value} className={classes.TabPanel}>
                <p>
                    This label will be returned for the following betOffer:
                    <b> HalfTime/FullTime - id:8</b>.
                </p>
                <p>
                    <i>
                        The final outcome label is based on the outcome type,
                        event homeName and event awayName.
                    </i>
                </p>
                <p style={{ marginBottom: '0' }}>
                    <i>
                        It accepts a callback for translations, used to
                        translate some static text and the following keys needs
                        to be added to your own translations:
                    </i>
                </p>
                <ul>
                    <li>
                        <i>"mostpopular.outcomeLabel.home" : "Home"</i>
                    </li>
                    <li>
                        <i>"mostpopular.outcomeLabel.away": "Away"</i>
                    </li>
                    <li>
                        <i>"outcomes.draw": "Draw"</i>
                    </li>
                </ul>
                <p>
                    <i>
                        If the callback isn't provided it will fallback to the
                        english text.
                    </i>
                </p>
                <CodeBlock
                    title={OutcomeLabelHalfTimeFullTime.title}
                    expectedOutput={OutcomeLabelHalfTimeFullTime.expectedOutput}
                    initialDisplayedCode={
                        OutcomeLabelHalfTimeFullTime.initialDisplayedCode
                    }
                    moreCode={OutcomeLabelHalfTimeFullTime.moreCode}
                />
            </TabPanel>
            <TabPanel index={8} value={value} className={classes.TabPanel}>
                <p>
                    This label will be returned for the following betOffer:
                    <b> Player Occurrence Line - id:127</b>.
                </p>
                <p>
                    <i>
                        If the outcome has the type OT_OVER, OT_OVER_EXACT or
                        OT_UNDER it will format the label based on participant,
                        outcome label and outcome line, otherwise it will be
                        based only on participant, outcome label.
                    </i>
                </p>
                <CodeBlock
                    title={OutcomeLabelPlayerOccurrenceLine.title}
                    expectedOutput={
                        OutcomeLabelPlayerOccurrenceLine.expectedOutput
                    }
                    initialDisplayedCode={
                        OutcomeLabelPlayerOccurrenceLine.initialDisplayedCode
                    }
                    moreCode={OutcomeLabelPlayerOccurrenceLine.moreCode}
                />
            </TabPanel>
            <TabPanel index={9} value={value} className={classes.TabPanel}>
                <p>
                    If the betOffer doesn't have any of the types detailed in
                    the other examples, the default label is actually the
                    outcome.label received from the API.
                </p>
                <CodeBlock
                    title={OutcomeLabelDefault.title}
                    expectedOutput={OutcomeLabelDefault.expectedOutput}
                    initialDisplayedCode={
                        OutcomeLabelDefault.initialDisplayedCode
                    }
                    moreCode={OutcomeLabelDefault.moreCode}
                />
            </TabPanel>
        </div>
    );
};

export default OutcomeLabelTabs;
